import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AuthLayout } from '../components';
import Homescreen_icon from '../../home/assets/Homescreen.png';
import aexonLogo from '../../dashboard/assets/logo.png';
import { Button } from '@/components';
import { verifySubscriptionPayment } from '../api';
import {
  AUTH_BUTTON_CLASS,
  AUTH_FORM_CARD_CLASS,
  AUTH_SUBTITLE_CLASS,
  AUTH_TITLE_CLASS,
} from '../lib/authStyles';

export function PaymentSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const { subscriptionId, paymentId, signature, amount, planName } =
    location.state || {};
  const companyId = localStorage.getItem('company_id');

  const [seconds, setSeconds] = useState(5);
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying' | 'success' | 'failed'
  const [errorMsg, setErrorMsg] = useState('');

  // Call verify API on mount
  useEffect(() => {
    if (!subscriptionId || !paymentId || !signature || !companyId) {
      setVerificationStatus('failed');
      setErrorMsg('Required payment verification credentials are missing.');
      return;
    }

    const verifyPayment = async () => {
      try {
        await verifySubscriptionPayment({
          companyId,
          subscriptionId,
          paymentId,
          signature,
        });
        setVerificationStatus('success');
        toast.success('Payment verified & subscription activated!');
      } catch (err) {
        console.error('Payment verification error:', err);
        setVerificationStatus('failed');
        setErrorMsg(
          err?.response?.data?.detail ||
            err?.message ||
            'Payment verification failed. Please contact support.',
        );
        toast.error('Payment verification failed.');
      }
    };

    verifyPayment();
  }, [subscriptionId, paymentId, signature, companyId]);

  const handleBack = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard', { replace: true });
  };

  // Timer logic for redirection (only when success)
  useEffect(() => {
    if (verificationStatus !== 'success') return;

    if (seconds <= 0) {
      navigate('/dashboard', { replace: true });
      return;
    }
    const interval = setInterval(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [seconds, navigate, verificationStatus]);

  const getNextBillingDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Countdown timer circle calculations
  const totalDuration = 5;
  const radius = 24;
  const strokeWidth = 3;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    circumference - (seconds / totalDuration) * circumference;

  return (
    <AuthLayout rightImage={Homescreen_icon} rightAlt="Aexon Assistant Mockup">
      <div className="animate-fadeIn">
        <h1 className={AUTH_TITLE_CLASS}>
          {verificationStatus === 'verifying' && 'Verifying Payment...'}
          {verificationStatus === 'success' && 'Payment Successful!'}
          {verificationStatus === 'failed' && 'Payment Verification Failed'}
        </h1>
        <h2 className={AUTH_SUBTITLE_CLASS}>
          {verificationStatus === 'verifying' &&
            'Please wait while we confirm your payment details with our billing server...'}
          {verificationStatus === 'success' && (
            <>
              Your subscription to{' '}
              <span className="text-primary font-medium font-sf-pro-display">
                {planName || 'WhatsApp Business Pro'}
              </span>{' '}
              is now active. A receipt has been sent to your email.
            </>
          )}
          {verificationStatus === 'failed' && (
            <>
              We could not verify your payment. {errorMsg} If money was
              deducted, it will be automatically refunded.
            </>
          )}
        </h2>

        <div className={`${AUTH_FORM_CARD_CLASS} gap-6`}>
          {verificationStatus === 'verifying' && (
            <div className="h-64 flex flex-col items-center justify-center gap-4">
              <svg
                className="animate-spin h-10 w-10 text-primary"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <p className="text-text-secondary font-sf-pro-display text-h6-medium">
                Verifying transaction...
              </p>
            </div>
          )}

          {verificationStatus === 'failed' && (
            <div className="flex flex-col gap-6">
              <div className="w-full bg-red-500/10 border border-red-500/30 rounded-input p-6 flex flex-col gap-3 font-sf-pro-display text-center">
                <span className="text-red-500 text-body-lg-medium">
                  Verification Error
                </span>
                <p className="text-text-secondary text-h6">
                  {errorMsg ||
                    'We encountered an error verifying your signature.'}
                </p>
              </div>
              <div className="flex justify-center">
                <Button
                  size="lg"
                  type="button"
                  variant="primary"
                  className={`${AUTH_BUTTON_CLASS} w-full sm:w-[240px] h-[48px]`}
                  onClick={() => navigate('/whatsapp-activated')}
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}

          {verificationStatus === 'success' && (
            <>
              <div className="w-full bg-[#F3F5F3] border border-primary/30 rounded-2xl p-5 sm:p-6 flex flex-col gap-4 font-sf-pro-display">
                <div className="flex justify-between items-center gap-4">
                  <span className="text-text-secondary text-h6-medium shrink-0">
                    Transaction ID
                  </span>
                  <span className="text-text-primary text-h6-medium text-right break-all tabular-nums">
                    {paymentId || 'pay_dev_activated'}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <span className="text-text-secondary text-h6-medium shrink-0">
                    Amount Paid
                  </span>
                  <span className="text-text-primary text-h6-medium text-right tabular-nums">
                    ₹
                    {amount
                      ? (Number(amount) * 1.18).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '2,948.82'}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <span className="text-text-secondary text-h6-medium shrink-0">
                    Plan
                  </span>
                  <span className="text-text-primary text-h6-medium text-right">
                    {planName || 'WhatsApp Business Pro'}
                  </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                  <span className="text-text-secondary text-h6-medium shrink-0">
                    Next billing date
                  </span>
                  <span className="text-text-primary text-h6-medium text-right">
                    {getNextBillingDate()}
                  </span>
                </div>
              </div>

              <div className="w-full flex justify-center">
                <Button
                  size="lg"
                  type="button"
                  variant="primary"
                  className={`${AUTH_BUTTON_CLASS} w-full sm:w-[240px] h-[48px]`}
                  onClick={() => {
                    const companyName =
                      localStorage.getItem('company_name') || 'Your Company';
                    const txnId = paymentId || 'N/A';
                    const baseAmount = amount
                      ? Number(amount).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '2,499.00';
                    const gstAmount = amount
                      ? (Number(amount) * 0.18).toLocaleString('en-IN', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '449.82';
                    const paidAmount = amount
                      ? `₹${(Number(amount) * 1.18).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      : '₹2,948.82';
                    const plan = planName || 'WhatsApp Business Pro';
                    const nextBilling = getNextBillingDate();
                    const receiptDate = new Date().toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    });

                    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Payment Receipt — Aexon</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
      background: #eef0f2;
      color: #1e293b;
      padding: 48px 24px;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .page {
      max-width: 680px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid #e2e8f0;
    }

    /* ── HEADER (letterhead) ── */
    .header {
      padding: 36px 40px 24px;
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
    }
    .header-logo img {
      height: 34px;
      width: auto;
      object-fit: contain;
    }
    .header-logo span {
      display: block;
      margin-top: 6px;
      font-size: 11px;
      color: #94a3b8;
      letter-spacing: 0.05em;
    }
    .header-title { text-align: right; }
    .header-title h1 {
      font-size: 20px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    .header-title p {
      font-size: 12px;
      color: #94a3b8;
      margin-top: 4px;
    }

    /* ── ACCENT RULE ── */
    .accent-bar { height: 2px; background: #50AB3D; margin: 0 40px; }

    /* ── META ROW (Receipt No, Date, Status) ── */
    .meta-row {
      padding: 18px 40px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #e2e8f0;
    }
    .meta-item { text-align: left; }
    .meta-item .ml { font-size: 10px; font-weight: 600; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.07em; margin-bottom: 3px; }
    .meta-item .mv { font-size: 13px; font-weight: 600; color: #1e293b; }
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      color: #2F8E29;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }
    .status-dot { width: 6px; height: 6px; background: #50AB3D; border-radius: 50%; }

    /* ── BODY ── */
    .body { padding: 32px 40px; }

    /* ── BILL TO / RECEIPT INFO ── */
    .bill-row {
      display: flex;
      justify-content: space-between;
      gap: 32px;
      margin-bottom: 32px;
    }
    .bill-to, .receipt-info { flex: 1; }
    .block-label {
      font-size: 10px;
      font-weight: 700;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 10px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e2e8f0;
      display: block;
    }
    .bill-to .company { font-size: 15px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
    .bill-to .sub { font-size: 13px; color: #64748b; line-height: 1.6; }
    .receipt-info { text-align: right; }
    .receipt-info .ri-row { display: flex; justify-content: flex-end; gap: 16px; margin-bottom: 5px; }
    .receipt-info .ri-label { font-size: 12px; color: #94a3b8; font-weight: 500; }
    .receipt-info .ri-val { font-size: 12px; font-weight: 600; color: #1e293b; min-width: 140px; text-align: right; word-break: break-all; }

    /* ── LINE ITEMS TABLE ── */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
      font-size: 13px;
    }
    thead th {
      padding: 10px 4px;
      font-size: 11px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      text-align: left;
      border-bottom: 2px solid #0f172a;
    }
    thead th:last-child { text-align: right; }
    tbody td {
      padding: 14px 4px;
      color: #334155;
      border-bottom: 1px solid #f1f5f9;
      vertical-align: middle;
    }
    tbody td:last-child { text-align: right; font-weight: 600; color: #0f172a; }
    .desc-main { font-weight: 600; color: #0f172a; margin-bottom: 2px; }
    .desc-sub { font-size: 11px; color: #94a3b8; }

    /* ── TOTALS ── */
    .totals-wrap {
      display: flex;
      justify-content: flex-end;
    }
    .totals {
      width: 280px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      font-size: 13px;
    }
    .total-row .tl { color: #64748b; }
    .total-row .tv { font-weight: 600; color: #1e293b; }
    .grand-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0 0;
      margin-top: 6px;
      border-top: 2px solid #0f172a;
    }
    .grand-total .tl { font-size: 13px; font-weight: 700; color: #0f172a; }
    .grand-total .tv { font-size: 20px; font-weight: 800; color: #2F8E29; letter-spacing: -0.3px; }

    /* ── NOTES ── */
    .notes {
      margin-top: 32px;
      padding: 14px 16px;
      background: #f8fafc;
      border-left: 2px solid #50AB3D;
      font-size: 12px;
      color: #64748b;
      line-height: 1.6;
    }

    /* ── FOOTER ── */
    .footer {
      padding: 20px 40px;
      border-top: 1px solid #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .footer-left { font-size: 11px; color: #94a3b8; }
    .footer-right { text-align: right; font-size: 11px; color: #94a3b8; }
    .footer-right strong { color: #64748b; font-weight: 600; }

    @media print {
      body { background: #fff; padding: 0; }
      .page { border: none; max-width: 100%; }
    }
  </style>
</head>
<body>
  <div class="page">

    <!-- HEADER -->
    <div class="header">
      <div class="header-logo">
        <img src="${aexonLogo}" alt="Aexon" />
        <span>Aexon Systems</span>
      </div>
      <div class="header-title">
        <h1>PAYMENT RECEIPT</h1>
        <p>Official Tax Invoice</p>
      </div>
    </div>

    <!-- GREEN ACCENT -->
    <div class="accent-bar"></div>

    <!-- META ROW -->
    <div class="meta-row">
      <div class="meta-item">
        <div class="ml">Receipt No.</div>
        <div class="mv">#${txnId.slice(-8).toUpperCase()}</div>
      </div>
      <div class="meta-item">
        <div class="ml">Date</div>
        <div class="mv">${receiptDate}</div>
      </div>
      <div class="meta-item">
        <div class="ml">Status</div>
        <div class="mv">
          <span class="status-badge"><span class="status-dot"></span>PAID</span>
        </div>
      </div>
    </div>

    <!-- BODY -->
    <div class="body">

      <!-- BILL TO / RECEIPT INFO -->
      <div class="bill-row">
        <div class="bill-to">
          <div class="block-label">Bill To</div>
          <div class="company">${companyName}</div>
          <div class="sub">Subscription: ${plan}<br/>Next Billing: ${nextBilling}</div>
        </div>
        <div class="receipt-info">
          <div class="block-label">Transaction Details</div>
          <div class="ri-row">
            <span class="ri-label">Transaction ID</span>
            <span class="ri-val">${txnId}</span>
          </div>
          <div class="ri-row">
            <span class="ri-label">Payment Method</span>
            <span class="ri-val">Razorpay</span>
          </div>
        </div>
      </div>

      <!-- LINE ITEMS TABLE -->
      <table>
        <thead>
          <tr>
            <th style="width:46%">Description</th>
            <th style="width:12%; text-align:center">Qty</th>
            <th style="width:20%; text-align:right">Unit Price</th>
            <th style="width:22%">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <div class="desc-main">${plan}</div>
              <div class="desc-sub">Monthly Subscription &bull; WhatsApp Business API</div>
            </td>
            <td style="text-align:center">1</td>
            <td style="text-align:right">&#8377;${baseAmount}</td>
            <td>&#8377;${baseAmount}</td>
          </tr>
        </tbody>
      </table>

      <!-- TOTALS -->
      <div class="totals-wrap">
        <div class="totals">
          <div class="total-row">
            <span class="tl">Subtotal</span>
            <span class="tv">&#8377;${baseAmount}</span>
          </div>
          <div class="total-row">
            <span class="tl">GST (18%)</span>
            <span class="tv">&#8377;${gstAmount}</span>
          </div>
          <div class="grand-total">
            <span class="tl">Total Paid</span>
            <span class="tv">${paidAmount}</span>
          </div>
        </div>
      </div>

      <!-- NOTES -->
      <div class="notes">
        <strong style="color:#334155">Note:</strong> This is a system-generated receipt and does not require a physical signature.
        Your subscription is now active. A copy of this receipt has been sent to your registered email address.
      </div>

    </div>

    <!-- FOOTER -->
    <div class="footer">
      <div class="footer-left">&#169; ${new Date().getFullYear()} Aexon Systems &bull; All rights reserved.</div>
      <div class="footer-right"><strong>support@aexon.in</strong> &bull; +91 81601 08049 &bull; Thank you for your business</div>
    </div>

  </div>
</body>
</html>`;

                    const iframe = document.createElement('iframe');
                    iframe.style.position = 'fixed';
                    iframe.style.top = '-9999px';
                    iframe.style.left = '-9999px';
                    iframe.style.width = '0';
                    iframe.style.height = '0';
                    document.body.appendChild(iframe);
                    iframe.contentDocument.open();
                    iframe.contentDocument.write(html);
                    iframe.contentDocument.close();
                    iframe.onload = () => {
                      iframe.contentWindow.focus();
                      iframe.contentWindow.print();
                      setTimeout(() => document.body.removeChild(iframe), 1000);
                    };
                  }}
                >
                  <svg
                    className="w-5 h-5 shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
                    />
                  </svg>
                  Download Receipt
                </Button>
              </div>

              <div className="flex flex-col items-center justify-center gap-3">
                <div className="relative w-20 h-20 flex items-center justify-center">
                  <svg
                    className="absolute inset-0 w-full h-full transform -rotate-90 text-primary"
                    viewBox="0 0 60 60"
                  >
                    <circle
                      cx="30"
                      cy="30"
                      r={radius}
                      fill="transparent"
                      stroke="currentColor"
                      strokeOpacity="0.15"
                      strokeWidth={strokeWidth}
                    />
                    <circle
                      cx="30"
                      cy="30"
                      r={radius}
                      fill="transparent"
                      stroke="currentColor"
                      strokeWidth={strokeWidth}
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  <span className="font-sf-pro-display text-h3-medium text-text-primary relative z-10">
                    {seconds > 0 ? seconds : 0}
                  </span>
                </div>
                <p className="text-text-secondary font-sf-pro-display text-h6 text-center">
                  Redirecting to your dashboard...
                </p>
              </div>

              <div className="w-full flex justify-center">
                <Button
                  size="lg"
                  type="button"
                  variant="primary"
                  className={`${AUTH_BUTTON_CLASS} w-full sm:w-[280px] h-[48px]`}
                  onClick={handleGoToDashboard}
                >
                  Go to dashboard now
                  <span>→</span>
                </Button>
              </div>
            </>
          )}

          <div className="text-center mt-2">
            <button
              type="button"
              onClick={handleBack}
              className="text-body-lg font-normal font-sf-pro-display text-text-primary hover:text-primary transition-colors cursor-pointer inline-flex items-center gap-1.5"
            >
              <svg
                className="w-4 h-4 inline"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
              <span>Back to Login</span>
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
